import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '../lib/utils.js';
import { peerFocusRing } from '../lib/focus.js';

export interface SwitchProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'role'> {
  /**
   * Applied to the OUTER wrapper, not to the input. The input is a transparent
   * overlay (see below) — styling it would paint nothing.
   */
  className?: string;
}

/* ===========================================================================
 * Switch — a toggle whose change takes effect IMMEDIATELY.
 *
 * WHY THIS IS NOT `<Checkbox>`. A checkbox states an intent that some later
 * Save commits; a switch *is* the action. ARIA draws the same line, and so do
 * the two call sites this was built for (settings rows and a report-schedule
 * enable), which is why both had been left hand-rolled as
 * `<input type="checkbox" role="switch">` with an ad-hoc `h-5 w-5` box: the
 * Checkbox primitive was the wrong component and everyone knew it, so nobody
 * used it. This gives the role its own control.
 *
 * WHY A NATIVE INPUT AND NOT `<button role="switch">`. Both are legitimate
 * ARIA switches, but only one of them can be labelled by a `<label htmlFor>`:
 * `for`/`htmlFor` targets *labelable* elements, and `<button>` is not one.
 * Both call sites wrap the control in a `<label htmlFor={id}>` that carries the
 * 44px hit area and forwards its clicks — with a button that label would be
 * inert markup and the whole row would stop toggling. The input also keeps
 * `checked` / `onChange(e.target.checked)` working unchanged, so migrating the
 * call sites is a swap rather than a rewrite. `role="switch"` on a real
 * checkbox is the ARIA-blessed pairing (same checked semantics, different
 * announced role), so accessibility stays free — the same trade Checkbox.tsx
 * makes for the same reason.
 *
 * ANATOMY. The input is `appearance-none opacity-0` and stretched over the
 * whole wrapper; the track and the thumb are two `aria-hidden` siblings AFTER
 * it, driven by `peer-checked:` / `peer-focus-visible:` / `peer-disabled:`.
 * They must be siblings, not descendants of one another: `peer-*` compiles to
 * a `~` sibling combinator, which cannot reach a grandchild. Painting the
 * input itself is not an option — a checkbox is a replaced element the browser
 * draws (the same constraint Checkbox.tsx documents), so a track-and-thumb has
 * to be built beside it, not on it.
 *
 * COLOUR, measured (WCAG 2.1 relative luminance, light / dark):
 *
 *   OFF  track `bg-input`      vs card 3.60 / 3.08   vs page 3.09 / 3.47
 *        thumb `bg-surface`    on that track 3.60 / 3.08
 *   ON   track `bg-primary`    vs card 7.63 / 9.61   vs page 6.54 / 10.85
 *        thumb `bg-primary-foreground` on that track 7.63 / 7.71
 *
 * so every boundary WCAG 1.4.11 cares about — control against its ground, and
 * thumb against its track — clears 3:1 in both themes, in both states.
 *
 * The off track borrows `--input` as a FILL rather than as its usual outline.
 * That is deliberate: `--input` is the one neutral solved against the *harder*
 * of the two grounds it can land on (see the note in index.css), which is
 * exactly the guarantee a track needs. The obvious alternative, `bg-muted`
 * with a `border-input` hairline, was measured and rejected — the track reads
 * 1.22 / 1.15 against the card, so the thumb has nowhere to live: a
 * `bg-surface` thumb vanishes into it (1.22) and a `bg-input` thumb misses the
 * bar (2.95 / 2.68). A filled track is the only version that passes.
 *
 * The ON thumb is `primary-foreground`, not `primary-on-surface`: the track is
 * a SOLID brand fill, which is precisely the role `-foreground` names. (The
 * inverse mistake — `-foreground` on a pale tint — measures ~1.2:1 in dark.)
 *
 * GEOMETRY. 44×24 track, 20px thumb, 2px inset, so the travel is exactly
 * 44 − 20 − 2 − 2 = 20px (`translate-x-5`).
 *
 * RTL. The whole control is mirrored with `rtl:-scale-x-100` — the house
 * pattern for a graphic that means a direction (Sidebar, MonthPicker, StatTile
 * all do it). That keeps the internals written in one direction only: without
 * it every `left-*`/`translate-x-*` here would need a stacked `rtl:peer-*:`
 * counterpart, and stacked variants compile to selectors that are easy to get
 * subtly wrong. Nothing inside the switch is text, so nothing is mirrored that
 * shouldn't be.
 * ======================================================================== */

/* The 44px floor (WCAG 2.5.5) lands on the WRAPPER, so the finger target grows
 * on a camp tablet while the painted track stays 24px — sizing the control
 * itself would just draw a fat switch. No negative margin to claw the space
 * back, unlike Checkbox's bare box: that one has to survive a dense roster
 * table, whereas a switch is a row-level control and both call sites already
 * reserve `min-h-touch` for the row. */
const wrapperClass = cn(
  'relative inline-flex h-6 w-11 shrink-0 items-center',
  '[@media(pointer:coarse)]:min-h-touch',
  'rtl:-scale-x-100'
);

/* The real control, invisible and covering the wrapper, so the switch is
 * directly clickable on its own — it does not depend on a caller supplying a
 * `<label>` — while keyboard focus, `checked` and form participation stay
 * native. */
const inputClass = cn(
  'peer absolute inset-0 z-10 h-full w-full cursor-pointer appearance-none rounded-full opacity-0',
  'disabled:cursor-not-allowed'
);

const trackClass = cn(
  'pointer-events-none absolute inset-x-0 top-1/2 h-6 -translate-y-1/2 rounded-full',
  'bg-input peer-checked:bg-primary',
  // Only colour transitions — never geometry, which would animate layout.
  'transition-colors duration-fast ease-standard',
  // Same focus treatment as Button/Checkbox: a 2px ring held 2px off the
  // control. It renders on the track because the input it belongs to has no
  // paint of its own.
  peerFocusRing,
  'peer-disabled:opacity-50'
);

const thumbClass = cn(
  'pointer-events-none absolute left-0.5 top-1/2 h-5 w-5 -translate-y-1/2 rounded-full',
  'bg-surface peer-checked:bg-primary-foreground',
  'peer-checked:translate-x-5',
  'transition-[transform,background-color] duration-fast ease-standard',
  'peer-disabled:opacity-50'
);

/**
 * An ARIA switch. Renders the control ONLY — no label, by design.
 *
 * Both known call sites already own a `<label htmlFor>` (one puts an "On"/"Off"
 * readout *before* the switch, the other its caption *after*), and a `label`
 * prop here would nest a second `<label>` inside theirs — the invalid-HTML trap
 * `checkboxBoxClass` exists to route around. Give it an `id` and point a label
 * at it, or pass `aria-label` when it stands alone.
 */
export const Switch = forwardRef<HTMLInputElement, SwitchProps>(
  ({ className, ...props }, ref) => (
    <span className={cn(wrapperClass, className)}>
      <input ref={ref} type="checkbox" role="switch" className={inputClass} {...props} />
      <span className={trackClass} aria-hidden="true" />
      <span className={thumbClass} aria-hidden="true" />
    </span>
  )
);
Switch.displayName = 'Switch';
